
import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { Patient } from '@/types/doctor';
import { MessageSquare, Eye, Calendar, Clipboard } from 'lucide-react';
import { useToast } from '@/components/ui/use-toast';

interface DashboardPatientListProps {
  patients: Patient[];
  type: 'new' | 'pending' | 'followup';
}

const DashboardPatientList: React.FC<DashboardPatientListProps> = ({ 
  patients,
  type
}) => {
  const navigate = useNavigate();
  const { toast } = useToast();

  const handleScheduleClick = (patientId: string) => {
    navigate(`/doctor-area/appointments?patientId=${patientId}`);
  };

  const handleViewItinerary = (patientId: string) => {
    navigate(`/doctor-area/case-file/${patientId}?tab=itinerary`);
  };

  const handleMessageClick = (patientId: string) => {
    navigate(`/doctor-area/patient-chat/${patientId}`);
  };

  const handleRescheduleClick = (patientId: string) => {
    navigate(`/doctor-area/appointments?reschedule=${patientId}`);
    
    toast({
      title: "Reschedule initiated",
      description: "Select a new date and time for this follow-up.",
      duration: 3000,
    });
  };

  if (patients.length === 0) {
    return (
      <div className="text-center py-6 text-muted-foreground">
        {type === 'new' && 'No new patients at the moment'}
        {type === 'pending' && 'No patients awaiting replies'}
        {type === 'followup' && 'No upcoming follow-ups'}
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {patients.map((patient) => (
        <div 
          key={patient.id} 
          className="flex flex-wrap sm:flex-nowrap justify-between items-center p-3 border rounded-lg gap-3"
        >
          <div className="flex items-center gap-3 w-full sm:w-auto">
            <div className="h-9 w-9 rounded-full bg-muted overflow-hidden flex-shrink-0">
              {patient.avatar ? (
                <img 
                  src={patient.avatar} 
                  alt={patient.name} 
                  className="h-full w-full object-cover"
                />
              ) : (
                <div className="h-full w-full flex items-center justify-center bg-primary/10 text-primary font-medium">
                  {patient.name.charAt(0)}
                </div>
              )}
            </div>
            <div className="min-w-0 flex-1">
              <h4 className="text-sm font-medium truncate">{patient.name}</h4>
              <div className="flex flex-col xs:flex-row xs:items-center gap-1 xs:gap-2">
                <p className="text-xs text-muted-foreground truncate">{patient.condition}</p>
                {patient.progress !== undefined && (
                  <>
                    <span className="hidden xs:inline text-xs text-muted-foreground">•</span>
                    <div className="text-xs text-muted-foreground flex items-center">
                      <Progress value={patient.progress} className="h-1 w-12 inline-block mr-1" />
                      <span>{patient.progress}%</span>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
          <div className="flex gap-2 w-full sm:w-auto justify-end">
            {type === 'new' && (
              <>
                <Button 
                  size="sm" 
                  variant="outline" 
                  className="flex-1 sm:flex-none" 
                  onClick={() => handleMessageClick(patient.id)}
                >
                  <MessageSquare className="h-3 w-3 mr-1" />
                  <span className="sm:hidden md:inline">Message</span>
                </Button>
                <Button 
                  size="sm" 
                  className="flex-1 sm:flex-none" 
                  onClick={() => navigate(`/doctor-area/case-file/${patient.id}`)}
                >
                  <Eye className="h-3 w-3 mr-1" />
                  <span className="sm:hidden md:inline">View</span>
                </Button>
              </>
            )}
            
            {type === 'pending' && (
              <>
                <Button 
                  size="sm" 
                  variant="outline" 
                  className="flex-1 sm:flex-none" 
                  onClick={() => handleMessageClick(patient.id)}
                >
                  <MessageSquare className="h-3 w-3 mr-1" />
                  <span className="sm:hidden md:inline">Reply</span>
                </Button>
                <Button 
                  size="sm" 
                  className="flex-1 sm:flex-none" 
                  onClick={() => navigate(`/doctor-area/case-file/${patient.id}`)}
                >
                  <Clipboard className="h-3 w-3 mr-1" />
                  <span className="sm:hidden md:inline">Details</span>
                </Button>
              </>
            )}
            
            {type === 'followup' && (
              <>
                <Button 
                  size="sm" 
                  variant="outline" 
                  className="flex-1 sm:flex-none" 
                  onClick={() => handleRescheduleClick(patient.id)}
                >
                  <Calendar className="h-3 w-3 mr-1" />
                  <span className="sm:hidden md:inline">Reschedule</span>
                </Button>
                <Button 
                  size="sm" 
                  variant="outline" 
                  className="flex-1 sm:flex-none" 
                  onClick={() => handleMessageClick(patient.id)}
                >
                  <MessageSquare className="h-3 w-3 mr-1" />
                  <span className="sm:hidden md:inline">Message</span>
                </Button>
                <Button 
                  size="sm" 
                  className="flex-1 sm:flex-none" 
                  onClick={() => navigate(`/doctor-area/case-file/${patient.id}`)}
                >
                  <Eye className="h-3 w-3 mr-1" />
                  <span className="sm:hidden md:inline">View</span>
                </Button>
              </>
            )}
          </div>
        </div>
      ))}
    </div>
  );
};

export default DashboardPatientList;
